import { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion'; // eslint-disable-line no-unused-vars
import {
  LogOut,
  Settings,
  Menu,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { RealtimeContext } from '../contexts/RealtimeContext';
import { useClock } from '../hooks/useClock';
import GlobalSearch from '../components/GlobalSearch';
import { NotificationBell } from '../components/Notifications/NotificationBell';
import FlipClock from '../components/dashboard/FlipClock';
import { cn } from '../utils/cn';

const dropdownVariants = {
  hidden: { opacity: 0, y: 6, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1 },
};

const ICON_BTN =
  'w-10 h-10 flex items-center justify-center text-slate-600 dark:text-slate-300 ' +
  'hover:bg-slate-200/70 dark:hover:bg-white/[0.06] hover:text-slate-900 dark:hover:text-white ' +
  'transition-[background-color,color,transform] duration-150 ease-out-expo active:scale-95 motion-reduce:active:scale-100';

// Scroll elevation: a 1px bottom hairline + soft drop shadow, applied once the
// page scrolls so the sticky bar reads as floating above the content. Built
// from box-shadow only (no border) so it adds no layout height.
const ELEVATED =
  'shadow-[0_1px_0_0_rgba(15,23,42,0.08),0_10px_30px_-20px_rgba(15,23,42,0.35)] ' +
  'dark:shadow-[0_1px_0_0_rgba(255,255,255,0.08),0_12px_32px_-22px_rgba(0,0,0,0.75)]';

export default function Header({ onMenuClick, overlay = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const realtime = useContext(RealtimeContext);
  const connected = !!realtime?.connected;
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);

  // Track page scroll on every route so the header can animate: it elevates
  // (hairline + shadow) once scrolled, and in overlay mode (dashboard) it also
  // fades from transparent to the solid bar so the toolbar stays legible.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 2);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const solid = !overlay || scrolled;

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  // Sticky, borderless bar that blends with the content column (the page scrolls
  // beneath it). A subtle blur + translucency keeps it legible over content.
  return (
    <header
      className={cn(
        'sticky top-0 z-20 backdrop-blur-md transition-[background-color,box-shadow] duration-300 ease-out-expo',
        solid ? 'bg-slate-50/85 dark:bg-slate-950/85' : 'bg-transparent',
        scrolled ? ELEVATED : 'shadow-none'
      )}
    >
      <div className="h-[72px] px-4 md:px-6 flex items-center gap-3">

        {/* Mobile menu toggle — hidden on desktop */}
        <button
          onClick={onMenuClick}
          className={cn(ICON_BTN, 'md:hidden flex-shrink-0')}
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Search — left-aligned, width-capped for readability */}
        <div className="flex-1 min-w-0">
          <div className="max-w-xl">
            <GlobalSearch />
          </div>
        </div>

        {/* Live clock — always present off the dashboard; on the dashboard it
            slides/fades in on scroll as the hero clock scrolls out of view. */}
        <HeaderClock visible={!overlay || scrolled} />

        {/* Right cluster — notifications · user (live status folds into the avatar dot) */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <NotificationBell />

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu((v) => !v)}
              aria-label="User menu"
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
              title={user?.fullName}
              className="group relative flex items-center justify-center transition-transform duration-150 ease-out-expo active:scale-95 motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 dark:focus-visible:ring-offset-slate-950 focus-visible:ring-primary-500 dark:focus-visible:ring-carbon-400 rounded-full"
            >
              {/* Avatar only — modern circle. Presence dot doubles as the realtime "live" indicator. */}
              <div className="relative">
                <div
                  className={cn(
                    'w-9 h-9 rounded-full bg-slate-900 dark:bg-white flex items-center justify-center ring-1 transition-[box-shadow]',
                    showUserMenu
                      ? 'ring-primary-500/70 dark:ring-carbon-400/70'
                      : 'ring-slate-300 group-hover:ring-slate-400 dark:ring-white/15 dark:group-hover:ring-white/30'
                  )}
                >
                  <span className="font-ledger-mono text-sm font-bold text-white dark:text-slate-900 leading-none select-none">
                    {user?.fullName?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <span
                  role="status"
                  aria-label={connected ? 'Realtime sync on' : 'Reconnecting'}
                  title={connected ? 'Live — realtime sync on' : 'Reconnecting…'}
                  className={cn(
                    'absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-slate-50 dark:ring-slate-950',
                    connected ? 'bg-emerald-500 motion-safe:animate-pulse' : 'bg-amber-500'
                  )}
                />
              </div>
            </button>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={dropdownVariants}
                  transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                  role="menu"
                  className="absolute right-0 top-full mt-2 w-72 origin-top-right overflow-hidden bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl shadow-slate-900/10"
                >
                  {/* Identity panel */}
                  <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/40" role="none">
                    <p className="font-ledger-mono text-xs font-bold uppercase tracking-[0.14em] text-primary-600 dark:text-carbon-400 mb-1.5">
                      Signed in as
                    </p>
                    <p className="font-serif text-base font-bold text-slate-900 dark:text-white truncate">{user?.fullName}</p>
                    <p className="font-ledger-mono text-xs text-slate-600 dark:text-slate-400 truncate mt-0.5">{user?.email}</p>
                  </div>

                  {/* Actions */}
                  <div className="py-1" role="none">
                    <button
                      onClick={() => { setShowUserMenu(false); navigate('/utility'); }}
                      role="menuitem"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left group transition-colors text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/[0.06]"
                    >
                      <Settings size={16} className="text-slate-500 group-hover:text-primary-600 dark:group-hover:text-carbon-400 flex-shrink-0" />
                      <span className="font-sans text-sm">Settings</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      role="menuitem"
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors text-slate-600 hover:text-red-600 hover:bg-red-50 dark:text-slate-400 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                    >
                      <LogOut size={16} className="flex-shrink-0" />
                      <span className="font-sans text-sm">Sign out</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>
    </header>
  );
}

/**
 * HeaderClock — the compact split-flap clock that lives in the header. Owns its
 * own tick so only it re-renders each second (not the whole Header). `visible`
 * drives a fade+slide transition: it reserves its space when hidden (no layout
 * shift) and animates in when shown — the handoff from the dashboard hero clock.
 */
function HeaderClock({ visible }) {
  const now = useClock(1000);
  return (
    <div
      aria-hidden={!visible}
      className={cn(
        'hidden md:block flex-shrink-0 transition-[opacity,transform] duration-300 ease-out-expo',
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
      )}
    >
      <FlipClock date={now} compact />
    </div>
  );
}
